/**
 * @file invoiceFromOrder.js
 * @version 1.3.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-01
 * @modified 2026-10-23
 * @description "Create Invoice" on a customer order: open the Create Invoice
 *              screen filled from the order (CreateInvoice reads the id on
 *              mount). Pricing is not a precondition — a price can be entered
 *              on the invoice and is written back to the order.
 *
 * 1.2.0 (2026-10-15, Cam): refused and waiting to be redelivered — not yet
 * (utils/redelivery; server orderInvoiceService).
 *
 * 1.3.0 (2026-10-23, Cam): a split order whose tonnage was added to the
 * customer's other order is invoiced there, not on its own.
 */

import { redeliveryInvoiceBlock } from './redelivery';
import { sourceInvoiceBlock } from './tonnageAdditions';

export const INVOICE_FROM_ORDER_KEY = 'invoiceFromOrderId';

/** Why an order cannot be invoiced yet, or null. */
export function invoiceBlockFor(order) {
  if (!order) return 'No order';
  // A split order's tonnage added to the customer's other order: invoiced there.
  const added = sourceInvoiceBlock(order.order_number, order.tonnage_additions?.added_to);
  if (added) return added;
  if (order.invoiced_status === 'Invoiced') return 'This order has already been invoiced';
  // Its draft invoice waits in Price Orders (server: orderInvoiceService.invoiceBlock).
  if (order.draft_invoice_no) {
    return `${order.order_number || 'This order'} has a draft invoice ${order.draft_invoice_no} waiting in Pricing & Costing — approve it there`;
  }
  // Refused on delivery and waiting to go again (utils/redelivery).
  const redelivery = redeliveryInvoiceBlock(order.order_number, order.redelivery);
  if (redelivery) return redelivery;
  const status = order.delivery_status;
  if (status !== 'Delivered') return `The order must be delivered first — delivery status is "${status || 'unknown'}"`;
  return null;
}

export function startInvoiceFromOrder(onNavigate, orderId) {
  if (!onNavigate || !orderId) return false;
  sessionStorage.setItem(INVOICE_FROM_ORDER_KEY, String(orderId));
  onNavigate('create-invoice');
  return true;
}
